<template>
  <g
    @mousemove="handleBoxHover"
    @mouseover="handleBoxHover"
    @dblclick="handleDoubleClick"
  >
    <rect
      :width="width"
      :height="height"
      :fill="colors.boxColor"
    />
    <text
      v-show="showSymbols && canTextFitInBox"
      :fill="colors.fontColor"
      text-anchor="middle"
      opacity="1"
      :font-size="fontSize"
      :transform="`translate(${width / 2}, ${height / 2})`"
    >
      <template v-if="canFitTwoLines">
        <tspan font-weight="900" x="0" opacity="1" y="0em" :style="{ fontFamily: Font.LATO}">{{ data.symbol }}</tspan>
        <tspan :font-size="fontSize * 0.7" x="0" class="value" opacity="1" :y="fontSize" :style="{ fontFamily: Font.LATO}">{{ data.calculatedValues.display }}</tspan>
      </template>
      <template v-else>
        <tspan font-weight="900" x="0" opacity="1" y="0.5em" :style="{ fontFamily: Font.LATO}">{{ data.symbol }}</tspan>
      </template>
    </text>
  </g>
</template>

<script lang="ts" setup>
  import { computed, toRefs } from 'vue';
  import { Font } from '@/ts/constants/fonts';
  import { MIN_FONT_SIZE, MAX_FONT_SIZE } from '@/ts/constants/fontSize';
  import { clamp } from '@/ts/util/util';
  import { useMarketCarpetContext } from '@/ts/marketCarpetContext';
  import type { LeafNode } from '@/ts/composables/useMarketCarpetData';
  import type { Box } from '@/ts/composables/useHoverBox';

  const props = defineProps<{
    width: number;
    height: number;
    data: LeafNode;
    parent: Box | null;
  }>();

  const { width, height, data, parent } = toRefs(props);
  const { showSymbols, setLeafBoxHoverData, getLeafColor } = useMarketCarpetContext();

  const fontSize = computed(() => {
    return getFontSize(data.value.symbol, 4);
  });

  const colors = computed(() => {
    const { boxColor, fontColor } = getLeafColor(data.value.calculatedValues.value);
    return {
      boxColor,
      fontColor
    };
  });

  const canFitTwoLines = computed(() => {
    return height.value > MIN_FONT_SIZE * 2 * 4; // TODO: magic numbers
  });

  const canTextFitInBox = computed(() => {
    return width.value >= data.value.symbol.length * MIN_FONT_SIZE;
  });

  function getFontSize(string: string, stringLengthCap: number = 0) {
    const fittingFont = Math.min(width.value, height.value) / Math.max(string.length, stringLengthCap);
    return clamp(MIN_FONT_SIZE, fittingFont, MAX_FONT_SIZE);
  }

  function handleDoubleClick() {
    const { symbol } = data.value;
    const url = new URL('/freecharts/symbolsummary.html', 'https://stockcharts.com');
    url.searchParams.set('sym', symbol);
    globalThis.open(url, '_blank');
  }

  function handleBoxHover(mouseEvent: MouseEvent) {
    setLeafBoxHoverData(data.value, mouseEvent, parent.value);
  }
</script>

<style scoped>
  text {
    user-select: none;
  }

  g:hover > rect {
    filter: brightness(1.2);
  }
</style>
